import {PhoneHQAvatar, PhoneHQIconAvatar, PhoneHQButton} from 'ch3-ui-lib';
import {Link} from "react-router-dom";
import {ChevronLeft} from "lucide-react";
import { useLocation } from 'react-router-dom';
import {useSelector} from "react-redux";
import {ReactNode, useEffect} from "react";
import TeamHeaderTitle from "../../pages/Team/TeamHeaderTitle.tsx";
import {getSelectedGroups, getSelectedNumber} from "../../store/features/Filters/filtersSlice.tsx";
import { selectUserId } from "../../store/features/Auth/authSlice.ts";
import {useFetchCurrentUserQuery, User} from "../../store/features/Users/userService";

import {activityStyles} from "../sidebar/helpers.ts";
import {getUserStatus, getUserStatusName} from "../../utilis/getStatus.ts";
import {FlagImage} from "react-international-phone";
import {getFlag, getFormatedPhoneNumber} from "../../utilis/msisdnUtilis.ts";
import {useStatusSubscription} from "../../hooks/useStatusSubscription";
import {useGroupsForFiltersQuery} from "../../store/features/groups/groupsService.ts";
import {isAdminSettingsPath} from "../../pages/Settings/settingsNav.ts";

interface HeaderRouteConfig {
    label?: string;
    icon?: string;
    buttonLabel?: string;
    buttonPath?: string;
    backButtonLabel?: string;
    backButtonPath?: string;
    avatar?: boolean;
    description?: string;
    isDynamic?: boolean;
    userName?: boolean;
    // Replaces the default icon + label title.
    title?: ReactNode;
}

const headerConfig: Record<string, HeaderRouteConfig> = {
    "/calls": { isDynamic: true },
    "/call/:number": { label: "Calls", icon: "1f919", isDynamic: true },
    "/contacts": {},
    "/contacts/:id": { label: "Edit Contact", icon: "1f4d2", backButtonLabel: "Back to contacts", backButtonPath: "/contacts" },
    "/contacts/new": { label: "New Contact", backButtonLabel: "Back to contacts", backButtonPath: "/contacts" },
    "/contacts/details/:id": {},
    "/team": { label: "Team", title: <TeamHeaderTitle/> },
    // Settings pages draw their own header inside the Settings layout.
    "/settings": {},
    "/settings/audio": {},
    "/settings/permissions": {},
    "/groups": {},
    "/groups/details/:id": {},
    "/groups/details/new": {},
    "/phoneNumbers": {},
    "/phoneNumbers/:id": {},
    "/profile": {},
    "/admin/integrations": { label: "Integrations", description: "Manage all connected apps and integrations to streamline team operations." },
    "/admin/report": { label: "Reports" },
    "/callQueues": {},
    "/callQueues/:id": {},
    "/admin/phoneNumbers": { label: "All phone numbers", icon: "1F4F1" },
    "/admin/phoneNumbers/:id": { backButtonLabel: "Back to phone numbers", backButtonPath:"/admin/phoneNumbers" },
    "/admin/groups": { label: "All groups", icon: "1F46A", buttonLabel: "Create new group", buttonPath: "/admin/groups/details/new" },
    "/admin/groups/details/:id": { backButtonLabel: "Back to groups", backButtonPath: "/admin/groups" },
    "/admin/groups/details/new": { label: "New group", backButtonLabel: "Back to groups", backButtonPath: "/admin/groups" },
    "/admin/integrations/google": { label: "Integrations", description: "Manage all connected apps and integrations to streamline team operations." },
    "/admin/integrations/pipedrive": { label: "Integrations", description: "PhoneHQ for Pipedrive is all about making your sales call process more efficient and convenient." },
    "/admin/integrations/google/connect": { label: "Integrations", description: "Manage all connected apps and integrations to streamline team operations." },
    "/admin/integrations/pipedrive/connect": { label: "Integrations", description: "PhoneHQ for Pipedrive is all about making your sales call process more efficient and convenient." },
    "/admin/integrations/google/list": { label: "Integrations", description: "Manage all connected apps and integrations to streamline team operations." },
    "/admin/integrations/pipedrive/settings": { label: "Integrations", description: "PhoneHQ for Pipedrive is all about making your sales call process more efficient and convenient." },
    "/admin/integrations/pipedrive/disconnected": { label: "Integrations", description: "PhoneHQ for Pipedrive is all about making your sales call process more efficient and convenient." },
    "/admin/integrations/google/disconnected": { label: "Integrations", description: "Manage all connected apps and integrations to streamline team operations." },
    "/recordings/:id": { label: "Recordings", description: "Download recording" },

    "/admin/webhook/list": {},
    "/admin/webhook/new": {},
    "/admin/webhook/:id": {},
    "/admin/webhook/:id/deliveries": {},

    "/transcript/:id": {  backButtonLabel: "Back to calls", backButtonPath: "/calls" },
    "/admin/team": { label: "Team members", icon: "1F64C", buttonLabel: "Invite new team member", buttonPath: "/admin/team-details/new" },
    "/admin/team-details/:id": { backButtonLabel: "Back to team", backButtonPath:"/admin/team" },
    "/admin/team-details/new": { backButtonLabel: "Back to team", backButtonPath:"/admin/team" },
    "/welcome": { label: "Hey", userName: true},
    "/admin/api/list": { backButtonLabel: "Back to integrations", backButtonPath:"/admin/integrations" },
    "/chat": {},
    "/admin/ens": {},
    "/admin/ens/alerts/:publicId": {},
    "/admin/ens/contacts": { backButtonLabel: "Back to ENS", backButtonPath: "/admin/ens" },
    "/ens/my-alerts": {},
};

const matchRoute = (pathname: string) => {
    const normalizedPath = pathname.replace(/\/+$/, "");

    if (headerConfig[normalizedPath]) {
        return headerConfig[normalizedPath];
    }

    const matchingRoute = Object.keys(headerConfig)
        .filter(route => route.includes(":"))
        .map(route => ({
            route,
            regex: new RegExp("^" + route.replace(/:\w+/g, "[^/]+") + "$"),
        }))
        .find(({ regex }) => regex.test(pathname));

    return matchingRoute ? headerConfig[matchingRoute.route] : headerConfig['/welcome'];
};

interface RootState {
    isMobile: boolean;
    callsData: {
        selectedPhoneNumber: {
            number: string;
            countryCode: string;
            flagIcon?: string;
        } | null;
    };
}

interface HeaderProps {
    /** Rendered inside the Settings frame of an admin page, next to the Settings list. */
    inSettingsFrame?: boolean;
}

const Header = ({inSettingsFrame = false}: HeaderProps) => {
    const location = useLocation();
    const { pathname } = location;
    const role = useSelector((state: any) => state.auth.role);
    const userId = useSelector(selectUserId);

    const {data: groupsData} = useGroupsForFiltersQuery({userId});

    const currentRoute = matchRoute(pathname);

    const isMobile = useSelector((state: RootState) => state.isMobile);

    const selectedPhoneNumber = useSelector(getSelectedNumber);
    const selectedGroup = useSelector(getSelectedGroups);

    const path = window.location.pathname;
    const isAdminPath = path.includes('admin')
    // Admin pages in Settings draw this header in their frame, not across the top.
    const isFramedAdminPage = isAdminSettingsPath(pathname);
    const isCallsPath = path === '/calls';
    const isTranscriptPath = path.includes('/transcript');
    const {data: user = {} as User} = useFetchCurrentUserQuery(userId);

    const { getStatus } = useStatusSubscription({
        userIds: user?.userId ? [user.userId] : []
    });

    const currentUserStatus = getStatus(user?.userId)

    useEffect(() => {
        const header = document.getElementById('header');
        if (header) {
            const banner = document.getElementById('superadmin-banner');
            const bannerHeight = banner ? Math.ceil(banner.offsetHeight) : 0;
            const headerHeight = Math.ceil(header.offsetHeight) + bannerHeight;
            document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        }
    }, [pathname, selectedGroup, selectedPhoneNumber, role]);

    if (!currentRoute || isFramedAdminPage !== inSettingsFrame) {
        return null;
    }

    // On Calls the header only appears for a chosen line or team (its Do Not Disturb state);
    // "All lines" lives in the lines column and needs no header.
    const shouldRenderDynamicContent = currentRoute.isDynamic &&
        pathname.includes('/calls') && Boolean(selectedPhoneNumber || selectedGroup);
    const isAvailableForUser = () => {
        if (isAdminPath && (role === 'SUPERADMIN' || role === 'ADMIN')) {
            return true;
        }
        return !(isAdminPath && role !== 'admin');
    }
    const group = groupsData?.find((group) => group.groupId === selectedGroup?.groupId);

    const renderDynamicCallsHeader = () => {
        if (!selectedPhoneNumber && !selectedGroup) {
            return (
                <>
                    <h1 className='m-0 text-title text-ink'>{currentRoute.label}</h1>
                </>
            );
        }

        return (
            <>

                {selectedPhoneNumber &&

                    // DND for a line now lives in the line's context menu in the Calls column (Dnd-LineMenu).
                    <div className='flex items-center gap-3'>
                        <PhoneHQIconAvatar icon={selectedPhoneNumber.icon || 'home'} color={selectedPhoneNumber.iconColor || 'blue'} size="24px"/>
                        <div>
                            <div className='text-lg font-semibold'>{selectedPhoneNumber.label ?? 'No name'}</div>
                            <div className='flex items-center gap-2 text-xs'>
                                <FlagImage iso2={getFlag(selectedPhoneNumber.msisdn)} size={16}/>
                                {getFormatedPhoneNumber(selectedPhoneNumber.msisdn, true)}
                            </div>
                        </div>
                    </div>
                }
                {group &&
                    <div className={'flex gap-3 items-center '}>
                        <div className='text-2xl items-end flex'>
                            <PhoneHQIconAvatar icon={group.icon ? group.icon : 'group'} color={group.iconColor ? group.iconColor : 'darkGrey'} size="24px"/>
                        </div>
                        <div>
                            <div className='flex items-center'>
                               <div className={'ml-2 text-lg font-semibold'}>
                                    {group.name }
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </>
        );
    };

    return (
        <>
            {isAvailableForUser() && <div id="header"
                  className={`sticky top-0 overflow-x-hidden ${inSettingsFrame ? '' : (isCallsPath || isTranscriptPath) ? 'ml-[300px] ' : 'ml-nav-rail '}` + 'border-line bg-surface'}>
                {currentRoute?.backButtonLabel &&
                    <div className='flex h-header items-center border-0 border-b border-solid border-line bg-surface px-4'>
                        {currentRoute.backButtonLabel && currentRoute.backButtonPath && (
                            <Link className='inline-flex h-7 items-center gap-1 rounded-chip pl-1 pr-2 text-callout text-ink-secondary hover:bg-surface-muted' to={currentRoute.backButtonPath}>
                                <ChevronLeft size={18} aria-hidden="true" className="!p-0 !text-current"/>
                                {currentRoute.backButtonLabel}
                            </Link>
                        )}
                    </div>}
                {(currentRoute.label || shouldRenderDynamicContent) &&
                    <div className={`relative z-10 flex min-h-header flex-wrap items-center justify-between gap-3 border-0 border-b border-solid border-line bg-surface px-6 py-2`}>
                        <div className='flex items-center gap-3'>
                            {currentRoute.avatar && <PhoneHQAvatar src={user.avatarUrl} name={user?.firstName + ' ' + user?.lastName} size="40px"/>}


                            {shouldRenderDynamicContent ? (
                                renderDynamicCallsHeader()
                            ) : (
                                currentRoute.title ? currentRoute.title : <>
                                    <div>
                                        <div className="flex gap-1 items-center">
                                            <h1 className={'m-0 text-title text-ink ' + (currentRoute.avatar ? '!text-body-strong' : '')}>{currentRoute.label}</h1>
                                            {Boolean(currentRoute.userName) &&
                                                <span className="text-title text-ink">{user.firstName}</span>
                                            }
                                        </div>
                                        {path.includes('profile') &&
                                            <div className={'text-xs flex font-normal items-center'}>
                                                <div
                                                    className={`${activityStyles({status: getUserStatus(currentUserStatus)})} w-3 h-3 rounded-full `}></div>
                                                <span
                                                    className="ml-1">{getUserStatusName(currentUserStatus)}</span>
                                            </div>
                                        }
                                        <div>
                                            {currentRoute.description && (
                                                <p className={`m-0 text-footnote text-ink-muted ${isMobile ? ' flex-wrap ' : ' no-wrap '}`}>
                                                    {currentRoute.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className='flex items-center gap-3'>
                            {currentRoute.buttonLabel && (
                                <Link to={`${currentRoute.buttonPath}`}>
                                    <PhoneHQButton size="sm">{currentRoute.buttonLabel}</PhoneHQButton>
                                </Link>
                            )}
                        </div>
                    </div>}
            </div>}
        </>

    );
};

export default Header;
